import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import BASE_URL from "api/Api";
import { FaArrowLeft, FaCity, FaSave } from "react-icons/fa";
import "assets/theme/admin_form.css";

const AddCity = () => {
  const navigate = useNavigate();
  const [districts, setDistricts] = useState([]);
  const [subdistricts, setSubdistricts] = useState([]);
  const [formData, setFormData] = useState({
    district_id: "",
    subdistrict_id: "",
    city_name: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Fetch districts on mount
  useEffect(() => {
    fetch(`${BASE_URL}super_admin/list_district`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => setDistricts(data.districts || data))
      .catch((err) => console.error(err));
  }, []);

  // Fetch subdistricts when district_id changes
  useEffect(() => {
    if (!formData.district_id) {
      setSubdistricts([]);
      return;
    }

    fetch(`${BASE_URL}super_admin/get_all_subdistrict?district_id=${formData.district_id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => setSubdistricts(data.subdistricts || data))
      .catch((err) => console.error(err));
  }, [formData.district_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "district_id") {
      setFormData((prev) => ({
        ...prev,
        district_id: value,
        subdistrict_id: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.district_id) {
      newErrors.district_id = "Please select a district";
    }
    if (!formData.subdistrict_id) {
      newErrors.subdistrict_id = "Please select a subdistrict";
    }
    if (!formData.city_name.trim()) {
      newErrors.city_name = "City name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;
    setSubmitting(true);

    try {
      const res = await fetch(`${BASE_URL}super_admin/add_cities`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          subdistrict_id: formData.subdistrict_id,
          city_name: formData.city_name,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      alert("City added successfully");
      navigate("/city-list");
    } catch (error) {
      alert(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="container-fluid py-3 admin-form-container">
        {/* Header Card with Left Back Arrow */}
        <div className="admin-form-header-card">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 position-relative" style={{ zIndex: 1 }}>
            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/city-list")}
                className="btn-admin-back"
                title="Back to City List"
              >
                <FaArrowLeft /> <span>मागे जा (Back)</span>
              </button>

              <div className="admin-header-icon">
                <FaCity />
              </div>

              <div>
                <h4 className="text-white fw-bold mb-0" style={{ fontSize: "1.25rem" }}>
                  नवीन शहर जोडा / Add City
                </h4>
                <p className="text-white-50 mb-0 small">
                  District → Subdistrict-wise city/town registration
                </p>
              </div>
            </div>

            <Link to="/city-list" className="btn btn-outline-light btn-sm fw-semibold rounded-pill px-3">
              City List / यादी
            </Link>
          </div>
        </div>

        {/* Form Card */}
        <div className="admin-form-card">
          <div className="card-body">
            <form onSubmit={handleSubmit} noValidate>
              <div className="row g-4">
                {/* 1. District Dropdown */}
                <div className="col-md-4">
                  <label className="admin-form-label">
                    Select District / जिल्हा निवडा <span className="required-star">*</span>
                  </label>

                  <select
                    className={`admin-form-select ${errors.district_id ? "border-danger" : ""}`}
                    name="district_id"
                    value={formData.district_id}
                    onChange={handleChange}
                  >
                    <option value="">Select District</option>
                    {districts.map((item) => (
                      <option key={item.district_id} value={item.district_id}>
                        {item.district_name}
                      </option>
                    ))}
                  </select>
                  {errors.district_id && (
                    <small className="text-danger fw-semibold d-block mt-1">{errors.district_id}</small>
                  )}
                </div>

                {/* 2. Subdistrict Dropdown */}
                <div className="col-md-4">
                  <label className="admin-form-label">
                    Select Subdistrict / तालुका निवडा <span className="required-star">*</span>
                  </label>

                  <select
                    className={`admin-form-select ${errors.subdistrict_id ? "border-danger" : ""}`}
                    name="subdistrict_id"
                    value={formData.subdistrict_id}
                    onChange={handleChange}
                    disabled={!formData.district_id}
                  >
                    <option value="">Select Subdistrict</option>
                    {subdistricts.map((item) => (
                      <option key={item.subdistrict_id} value={item.subdistrict_id}>
                        {item.subdistrict_name}
                      </option>
                    ))}
                  </select>
                  {errors.subdistrict_id && (
                    <small className="text-danger fw-semibold d-block mt-1">{errors.subdistrict_id}</small>
                  )}
                </div>

                {/* 3. City Name */}
                <div className="col-md-4">
                  <label className="admin-form-label">
                    City Name / शहराचे नाव <span className="required-star">*</span>
                  </label>

                  <input
                    type="text"
                    className={`admin-form-control ${errors.city_name ? "border-danger" : ""}`}
                    name="city_name"
                    placeholder="e.g. Pune, Mumbai, Nashik / पुणे, मुंबई, नाशिक"
                    value={formData.city_name}
                    onChange={handleChange}
                  />
                  {errors.city_name && (
                    <small className="text-danger fw-semibold d-block mt-1">{errors.city_name}</small>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex align-items-center justify-content-end gap-3 mt-4 pt-3 border-top">
                <Link to="/city-list" className="btn-admin-cancel">
                  रद्द करा (Cancel)
                </Link>
                <button
                  type="submit"
                  className="btn-admin-submit"
                  disabled={submitting}
                >
                  <FaSave />
                  <span>{submitting ? "जतन होत आहे..." : "जतन करा (Save City)"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddCity;
